import { useState } from "react";
import { PARENT_MILESTONES, saveParent, type LessonEvent } from "../../lib/storage";
import { MISSIONS } from "../../data/missions";
import { CHORE_EMOJIS } from "../../data/chores";
import { WORLDS, TIER_WORLDS } from "../../data/worlds";
import { BADGE_BY_ID } from "../../data/streakBadges";

const TIER_DISPLAY: Record<string, { label: string; emoji: string }> = {
  starter: { label: "Money Starter", emoji: "🌱" },
  builder: { label: "Money Builder", emoji: "🏗️" },
  master:  { label: "Money Master",  emoji: "🏆" },
};

function ProgressChart({ tier, lessonHistory, streak, earnedBadges, xp, coins }: { tier: string; lessonHistory: LessonEvent[]; streak: number; earnedBadges: string[]; xp: number; coins: number }) {
  const tierWorldIds = TIER_WORLDS[tier] ?? [];
  const tierWorlds   = WORLDS.filter(w => tierWorldIds.includes(w.id));

  const completedKeys = new Set(lessonHistory.map(e => `${e.worldId}-${e.lessonIdx}`));
  const totalLessons  = tierWorlds.reduce((s, w) => s + w.lessons.length, 0);
  const completedCount = tierWorlds.reduce((s, w) =>
    s + w.lessons.filter((_, i) => completedKeys.has(`${w.id}-${i}`)).length, 0);
  const overallPct = totalLessons > 0 ? completedCount / totalLessons : 0;

  const today     = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const weekAgo   = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const thisWeekEvents = lessonHistory.filter(e => e.completedAt.slice(0, 10) >= weekAgo);
  const thisWeekCount  = new Set(thisWeekEvents.map(e => `${e.worldId}-${e.lessonIdx}`)).size;

  const sorted = [...lessonHistory].sort((a, b) => b.completedAt.localeCompare(a.completedAt));
  const lastDate = sorted[0]?.completedAt.slice(0, 10);
  const lastActiveLabel = !lastDate ? "Never"
    : lastDate === today     ? "Today"
    : lastDate === yesterday ? "Yesterday"
    : new Date(lastDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

  type DayGroup = { label: string; events: LessonEvent[] };
  const dayMap = new Map<string, DayGroup>();
  const dayOrder: string[] = [];
  for (const evt of sorted.slice(0, 40)) {
    const d = evt.completedAt.slice(0, 10);
    if (!dayMap.has(d)) {
      const lbl = d === today ? "Today" : d === yesterday ? "Yesterday"
        : new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
      dayMap.set(d, { label: lbl, events: [] });
      dayOrder.push(d);
    }
    dayMap.get(d)!.events.push(evt);
  }
  const recentDays = dayOrder.slice(0, 5).map(d => dayMap.get(d)!);

  const tierDisplay = TIER_DISPLAY[tier] ?? { label: "Money Adventure", emoji: "💰" };
  const earnedBadgesList = earnedBadges.filter((id: string) => BADGE_BY_ID[id]).map((id: string) => BADGE_BY_ID[id]);

  const card: React.CSSProperties = { background: "#fff", borderRadius: 18, padding: "14px 16px", marginBottom: 14, border: "2px solid #ede9fe" };
  const sectionTitle: React.CSSProperties = { fontSize: 15, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 12 };

  return (
    <>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 22 }}>{tierDisplay.emoji}</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>{tierDisplay.label}</div>
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>Overall course progress</div>
          </div>
          <div style={{ marginLeft: "auto", fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#8b5cf6" }}>{Math.round(overallPct * 100)}%</div>
        </div>
        <div style={{ background: "#f3f4f6", borderRadius: 99, height: 14, overflow: "hidden", marginBottom: 6 }}>
          <div style={{ width: `${overallPct * 100}%`, height: "100%", background: "linear-gradient(90deg,#8b5cf6,#6d28d9)", borderRadius: 99, transition: "width 0.4s" }} />
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>{completedCount} of {totalLessons} lessons done</div>
      </div>

      <div style={{ ...card, border: "2px solid #fde68a" }}>
        <div style={sectionTitle}>📅 This Week</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { e: "🔥", l: "Streak",           v: `${streak} day${streak !== 1 ? "s" : ""}` },
            { e: "📚", l: "Lessons this week", v: String(thisWeekCount) },
            { e: "🪙", l: "Coins",             v: String(coins) },
            { e: "💎", l: "XP",                v: String(xp) },
          ].map(s => (
            <div key={s.l} style={{ background: "#fffbeb", borderRadius: 12, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 20 }}>{s.e}</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>{s.v}</div>
                <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 800 }}>{s.l}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Last active: <span style={{ color: "#374151", fontWeight: 900 }}>{lastActiveLabel}</span></div>
      </div>

      {tierWorlds.length > 0 && (
        <div style={card}>
          <div style={sectionTitle}>🗺️ World Progress</div>
          {tierWorlds.map(w => {
            const done  = w.lessons.filter((_: any, i: number) => completedKeys.has(`${w.id}-${i}`)).length;
            const total = w.lessons.length;
            const pct   = total > 0 ? done / total : 0;
            const complete = done === total;
            return (
              <div key={w.id} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 18 }}>{w.emoji}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 800, color: "#1f2937" }}>{w.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 900, color: complete ? "#10b981" : "#6b7280" }}>
                    {complete ? "✅ " : ""}{done}/{total}
                  </span>
                </div>
                <div style={{ background: "#f3f4f6", borderRadius: 99, height: 8, overflow: "hidden" }}>
                  <div style={{ width: `${pct * 100}%`, height: "100%", background: complete ? "linear-gradient(90deg,#10b981,#059669)" : `linear-gradient(90deg,${(w as any).color ?? "#8b5cf6"},${(w as any).dark ?? "#6d28d9"})`, borderRadius: 99, transition: "width 0.4s" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {recentDays.length > 0 && (
        <div style={card}>
          <div style={sectionTitle}>📋 Recent Activity</div>
          {recentDays.map(day => (
            <div key={day.label} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 900, color: "#8b5cf6", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{day.label}</div>
              {day.events.map((evt, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ fontSize: 16 }}>{evt.worldEmoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#1f2937" }}>{evt.title}</div>
                    <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700 }}>{evt.worldLabel}</div>
                  </div>
                  <span style={{ fontSize: 16 }}>✅</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {earnedBadgesList.length > 0 && (
        <div style={card}>
          <div style={sectionTitle}>🏅 Streak Badges</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {earnedBadgesList.map((b: { emoji: string; label: string }, i: number) => (
              <div key={i} style={{ background: "#f5f3ff", borderRadius: 14, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6, border: "2px solid #ede9fe" }}>
                <span style={{ fontSize: 22 }}>{b.emoji}</span>
                <span style={{ fontSize: 12, fontWeight: 900, color: "#6d28d9" }}>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function daysAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return "Never";
  const today     = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr >= today)     return "Today";
  if (dateStr >= yesterday) return "Yesterday";
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  return `${days} days ago`;
}

function nextDueDate(lastDeposited: string | null | undefined, frequency: string): string | null {
  if (!lastDeposited) return null;
  const d = new Date(lastDeposited);
  d.setDate(d.getDate() + (frequency === "weekly" ? 7 : frequency === "biweekly" ? 14 : 30));
  return d.toISOString().slice(0, 10);
}

function daysUntil(dateStr: string | null): number {
  if (!dateStr) return 0;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}

export default function ParentPortalScreen({ parentData, onSave, progress, xp, coins, streak, savingsData, missionsData, onVerifyMission, onDenyMission, onBack, choresData, onApproveChore, onDenyChore, onSaveChores, onDepositAllowance, rewardsData, tier, lessonHistory = [], earnedBadges = [] }: any) {
  const hasPin = !!parentData?.pin;
  const [view, setView] = useState<"pin" | "setup" | "dashboard">(hasPin ? "pin" : "setup");
  const [pinInput, setPinInput]   = useState("");
  const [pinSetup, setPinSetup]   = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinError, setPinError]   = useState("");

  function persist(next: any) { onSave(next); saveParent(next); }

  function submitPin() {
    if (pinInput === parentData.pin) { setView("dashboard"); setPinError(""); }
    else { setPinError("Incorrect PIN — try again."); setPinInput(""); }
  }

  function submitSetup() {
    if (!/^\d{4}$/.test(pinSetup)) { setPinError("PIN must be exactly 4 digits"); return; }
    if (pinSetup !== pinConfirm)   { setPinError("PINs don't match"); return; }
    persist({ ...parentData, pin: pinSetup });
    setView("dashboard");
  }

  function toggleMilestone(id: string) {
    persist({ ...parentData, milestones: { ...(parentData.milestones ?? {}), [id]: !parentData.milestones?.[id] } });
  }

  const lessons = Object.keys(progress ?? {}).length;
  const goals   = savingsData?.goals ?? [];
  const pendingIds: string[]   = missionsData?.pendingVerify ?? [];
  const pendingMissions = pendingIds.map((id: string) => MISSIONS.find(m => m.id === id)).filter(Boolean);

  // ── Allowance state ──
  const allowance = parentData?.allowance ?? {};
  const [showAllowanceSetup, setShowAllowanceSetup] = useState(false);
  const [newAllowanceAmt,  setNewAllowanceAmt]  = useState(allowance.amount?.toString() ?? "");
  const [newAllowanceFreq, setNewAllowanceFreq] = useState<"weekly"|"biweekly"|"monthly">(allowance.frequency ?? "weekly");

  function saveAllowance() {
    const amount = parseFloat(newAllowanceAmt);
    if (!amount || amount <= 0) return;
    persist({ ...parentData, allowance: { ...allowance, amount, frequency: newAllowanceFreq } });
    setShowAllowanceSetup(false);
  }

  const nextDue   = nextDueDate(allowance.lastDeposited, allowance.frequency ?? "weekly");
  const dueIn     = daysUntil(nextDue);
  const isOverdue = allowance.amount && nextDue && dueIn <= 0;
  const isDueSoon = allowance.amount && nextDue && dueIn > 0 && dueIn <= 2;

  // ── Activity summary ──
  const lastActive = rewardsData?.lastActive ?? null;
  const thisWeek   = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const weekChores = (choresData?.history ?? []).filter((h: any) => h.date >= thisWeek).slice(0, 4);
  const lessonsCount = Object.keys(progress ?? {}).length;
  const topGoal   = (savingsData?.goals ?? []).filter((g: any) => !g.completed)[0] ?? null;

  // ── Chores state ──
  const [showChoreSetup, setShowChoreSetup] = useState(false);
  const [newChoreName,  setNewChoreName]  = useState("");
  const [newChoreEmoji, setNewChoreEmoji] = useState("🧹");
  const [newChoreCoins, setNewChoreCoins] = useState("5");

  const chores: any[]        = choresData?.chores  ?? [];
  const pendingChoreIds: string[] = choresData?.pending ?? [];
  const pendingChores = pendingChoreIds.map((id: string) => chores.find((c: any) => c.id === id)).filter(Boolean);

  function addChore() {
    if (!newChoreName.trim()) return;
    const next = { ...choresData, chores: [...chores, { id: Date.now().toString(), emoji: newChoreEmoji, name: newChoreName.trim(), coins: Math.max(1, parseInt(newChoreCoins) || 5) }] };
    onSaveChores(next);
    setNewChoreName(""); setNewChoreCoins("5"); setNewChoreEmoji("🧹");
  }
  function removeChore(id: string) {
    onSaveChores({ ...choresData, chores: chores.filter((c: any) => c.id !== id), pending: pendingChoreIds.filter((x: string) => x !== id) });
  }

  // ── Shared PIN input style ──
  const pinInputStyle: any = {
    width: "100%", border: "2px solid #e5e7eb", borderRadius: 14, padding: "14px",
    fontSize: 28, letterSpacing: 10, textAlign: "center" as const, fontFamily: "Fredoka One",
    boxSizing: "border-box" as const, marginBottom: 12, outline: "none",
  };

  /* ── PIN SETUP ── */
  if (view === "setup") return (
    <div style={{ fontFamily: "Nunito", padding: "32px 20px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", marginBottom: 16 }}>← Back</button>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 52, marginBottom: 8 }}>👨‍👩‍👧</div>
        <div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>Parent Portal</div>
        <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700, marginTop: 6 }}>Create a 4-digit PIN so only you can access this view</div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>Create PIN</div>
      <input type="password" inputMode="numeric" maxLength={4} value={pinSetup} onChange={e => setPinSetup(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" style={pinInputStyle} />
      <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>Confirm PIN</div>
      <input type="password" inputMode="numeric" maxLength={4} value={pinConfirm} onChange={e => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" style={pinInputStyle} />
      {pinError && <div style={{ color: "#ef4444", fontWeight: 800, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{pinError}</div>}
      <button onClick={submitSetup} style={{ width: "100%", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", fontFamily: "Fredoka One" }}>Create PIN 🔐</button>
    </div>
  );

  /* ── PIN ENTRY ── */
  if (view === "pin") return (
    <div style={{ fontFamily: "Nunito", padding: "32px 20px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", marginBottom: 16 }}>← Back</button>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 52, marginBottom: 8 }}>🔐</div>
        <div style={{ fontSize: 26, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>Parent Portal</div>
        <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700, marginTop: 6 }}>Enter your 4-digit parent PIN</div>
      </div>
      <input type="password" inputMode="numeric" maxLength={4} value={pinInput}
        onChange={e => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
        onKeyDown={e => e.key === "Enter" && submitPin()}
        placeholder="••••" style={pinInputStyle} autoFocus />
      {pinError && <div style={{ color: "#ef4444", fontWeight: 800, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{pinError}</div>}
      <button onClick={submitPin} style={{ width: "100%", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", fontFamily: "Fredoka One" }}>Enter</button>
    </div>
  );

  /* ── DASHBOARD ── */
  return (
    <div style={{ fontFamily: "Nunito", paddingBottom: 60 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", padding: "22px 18px 20px", position: "relative", overflow: "hidden" }}>
        <button onClick={onBack} style={{ background: "#ffffff25", border: "none", borderRadius: 12, padding: "6px 12px", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer", marginBottom: 10 }}>← Back</button>
        <div style={{ position: "absolute", top: -20, right: -10, fontSize: 100, opacity: 0.1 }}>👨‍👩‍👧</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>👨‍👩‍👧 Parent View</div>
        <div style={{ fontSize: 13, color: "#e9d5ff", fontWeight: 700, marginTop: 4 }}>Here's how your child is doing</div>
      </div>

      <div style={{ padding: "16px" }}>

        {/* ── Activity Summary ── */}
        <div style={{ background: "#fff", borderRadius: 18, padding: "16px", marginBottom: 16, border: "2px solid #ede9fe" }}>
          <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 12 }}>📊 Activity Summary</div>

          {/* Last active */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, background: "#f5f3ff", borderRadius: 12, padding: "10px 14px" }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#374151" }}>Last active</span>
            <span style={{ fontSize: 13, fontWeight: 900, color: lastActive && daysAgo(lastActive) === "Today" ? "#059669" : lastActive && daysAgo(lastActive) === "Yesterday" ? "#d97706" : "#ef4444" }}>
              {daysAgo(lastActive)}
            </span>
          </div>

          {/* Stats row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            {[{ e: "🔥", l: "Streak", v: `${streak}d` }, { e: "⭐", l: "Lessons", v: lessonsCount }, { e: "🪙", l: "Coins", v: coins }].map(s => (
              <div key={s.l} style={{ background: "#f9fafb", borderRadius: 12, padding: "10px 6px", textAlign: "center" }}>
                <div style={{ fontSize: 20 }}>{s.e}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{s.v}</div>
                <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 800 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* This week's chore completions */}
          {weekChores.length > 0 ? (
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", marginBottom: 6 }}>THIS WEEK'S CHORES</div>
              {weekChores.map((h: any) => (
                <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <span style={{ fontSize: 18 }}>{h.choreEmoji}</span>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#374151" }}>{h.choreName}</span>
                  <span style={{ fontSize: 12, fontWeight: 900, color: "#f59e0b" }}>+{h.coins} 🪙</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 700, textAlign: "center", padding: "4px 0" }}>No chores completed this week yet</div>
          )}

          {/* Top savings goal */}
          {topGoal && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", marginBottom: 6 }}>TOP SAVINGS GOAL</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: "#374151" }}>{topGoal.emoji} {topGoal.name}</span>
                <span style={{ fontSize: 12, fontWeight: 900, color: "#10b981" }}>${topGoal.balance?.toFixed(2)} / ${topGoal.target?.toFixed(2)}</span>
              </div>
              <div style={{ background: "#f3f4f6", borderRadius: 20, height: 8, overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, topGoal.target > 0 ? topGoal.balance / topGoal.target * 100 : 0)}%`, height: "100%", background: "linear-gradient(90deg,#8b5cf6,#6d28d9)", borderRadius: 20 }} />
              </div>
            </div>
          )}
        </div>

        {/* ── Progress Chart ── */}
        {tier && lessonHistory.length > 0 && (
          <ProgressChart
            tier={tier}
            lessonHistory={lessonHistory}
            streak={streak}
            earnedBadges={earnedBadges}
            xp={xp}
            coins={coins}
          />
        )}

        {/* ── Allowance ── */}
        <div style={{ background: "#fff", borderRadius: 18, padding: "16px", marginBottom: 16, border: `2px solid ${isOverdue ? "#fca5a5" : isDueSoon ? "#fde68a" : "#d1fae5"}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>💵 Allowance</div>
            {isOverdue && <span style={{ fontSize: 11, fontWeight: 900, background: "#fee2e2", color: "#dc2626", borderRadius: 20, padding: "2px 10px" }}>Due Now!</span>}
            {isDueSoon && !isOverdue && <span style={{ fontSize: 11, fontWeight: 900, background: "#fef3c7", color: "#d97706", borderRadius: 20, padding: "2px 10px" }}>Due Soon</span>}
          </div>

          {allowance.amount ? (
            <>
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 12 }}>
                ${allowance.amount.toFixed(2)} / {allowance.frequency ?? "weekly"} • Last deposited: {daysAgo(allowance.lastDeposited)}
                {nextDue && <span> • Next due: {dueIn > 0 ? `in ${dueIn} day${dueIn !== 1 ? "s" : ""}` : "now"}</span>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { onDepositAllowance(allowance.amount); }}
                  style={{ flex: 1, background: isOverdue ? "linear-gradient(135deg,#ef4444,#dc2626)" : "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 12, padding: "11px", color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "Fredoka One" }}>
                  💵 Deposit ${allowance.amount.toFixed(2)}
                </button>
                <button onClick={() => { setShowAllowanceSetup(v => !v); setNewAllowanceAmt(allowance.amount.toString()); setNewAllowanceFreq(allowance.frequency ?? "weekly"); }}
                  style={{ background: "#f3f4f6", border: "none", borderRadius: 12, padding: "11px 14px", color: "#6b7280", fontWeight: 900, fontSize: 13, cursor: "pointer" }}>
                  Edit
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 12 }}>Set a recurring allowance — deposit it here and it splits automatically into your child's spending & savings.</div>
              <button onClick={() => setShowAllowanceSetup(v => !v)}
                style={{ width: "100%", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", border: "none", borderRadius: 12, padding: "11px", color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "Fredoka One" }}>
                Set Up Allowance ✨
              </button>
            </>
          )}

          {showAllowanceSetup && (
            <div style={{ marginTop: 14, background: "#f5f3ff", borderRadius: 14, padding: "14px" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", marginBottom: 6 }}>AMOUNT ($)</div>
              <input
                type="number" inputMode="decimal" value={newAllowanceAmt}
                onChange={e => setNewAllowanceAmt(e.target.value)}
                placeholder="e.g. 10.00"
                style={{ width: "100%", border: "1.5px solid #ddd6fe", borderRadius: 10, padding: "10px 12px", fontSize: 18, fontFamily: "Fredoka One", fontWeight: 900, color: "#1f2937", outline: "none", boxSizing: "border-box" as const, marginBottom: 12 }} />
              <div style={{ fontSize: 12, fontWeight: 800, color: "#374151", marginBottom: 6 }}>FREQUENCY</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {(["weekly","biweekly","monthly"] as const).map(f => (
                  <button key={f} onClick={() => setNewAllowanceFreq(f)}
                    style={{ flex: 1, background: newAllowanceFreq === f ? "#8b5cf6" : "#f3f4f6", border: `2px solid ${newAllowanceFreq === f ? "#6d28d9" : "#e5e7eb"}`, borderRadius: 10, padding: "8px 4px", color: newAllowanceFreq === f ? "#fff" : "#374151", fontWeight: 900, fontSize: 11, cursor: "pointer", fontFamily: "Nunito", textTransform: "capitalize" as const }}>
                    {f === "biweekly" ? "Every 2 wks" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
              <button onClick={saveAllowance} disabled={!newAllowanceAmt || parseFloat(newAllowanceAmt) <= 0}
                style={{ width: "100%", background: newAllowanceAmt && parseFloat(newAllowanceAmt) > 0 ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "10px", color: newAllowanceAmt && parseFloat(newAllowanceAmt) > 0 ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "Fredoka One" }}>
                Save Allowance ✓
              </button>
            </div>
          )}
        </div>

        {/* Pending mission approvals */}
        {pendingMissions.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 18, padding: "16px", marginBottom: 16, border: "2.5px solid #fbbf24" }}>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>🎯 Mission Approvals</div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 14 }}>
              Your child completed these missions — confirm they actually did them!
            </div>
            {pendingMissions.map((m: any) => (
              <div key={m.id} style={{ background: "#fffbeb", borderRadius: 14, padding: "12px 14px", marginBottom: 10, border: "2px solid #fde68a" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 26 }}>{m.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 900, color: "#1f2937" }}>{m.title}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginTop: 2 }}>{m.desc}</div>
                    <div style={{ fontSize: 11, fontWeight: 900, color: "#d97706", marginTop: 4 }}>+{m.xp} XP on approval</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => onVerifyMission(m.id, m.xp)}
                    style={{ flex: 1, background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 10, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                    ✅ Yes, they did it!
                  </button>
                  <button onClick={() => onDenyMission(m.id)}
                    style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "9px 14px", color: "#6b7280", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                    ✗ Not yet
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chore approvals */}
        {pendingChores.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 18, padding: "16px", marginBottom: 16, border: "2.5px solid #10b981" }}>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>🧹 Chore Approvals</div>
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 14 }}>
              Your child says they finished these chores — confirm they actually did them!
            </div>
            {pendingChores.map((c: any) => (
              <div key={c.id} style={{ background: "#f0fdf4", borderRadius: 14, padding: "12px 14px", marginBottom: 10, border: "2px solid #bbf7d0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 26 }}>{c.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 900, color: "#1f2937" }}>{c.name}</div>
                    <div style={{ fontSize: 11, fontWeight: 900, color: "#f59e0b", marginTop: 2 }}>+{c.coins} 🪙 on approval</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => onApproveChore(c.id, c.coins, c.name, c.emoji)}
                    style={{ flex: 1, background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 10, padding: "9px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                    ✅ Yes, they did it!
                  </button>
                  <button onClick={() => onDenyChore(c.id)}
                    style={{ background: "#f3f4f6", border: "none", borderRadius: 10, padding: "9px 14px", color: "#6b7280", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                    ✗ Not yet
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chore setup */}
        <div style={{ background: "#fff", borderRadius: 18, padding: "16px", marginBottom: 16, border: "2px solid #d1fae5" }}>
          <button onClick={() => setShowChoreSetup(v => !v)}
            style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>🧹 Chore Setup</div>
            <span style={{ fontSize: 14, color: "#9ca3af" }}>{showChoreSetup ? "▲" : "▼"}</span>
          </button>
          {showChoreSetup && (
            <div style={{ marginTop: 14 }}>
              {chores.length === 0 ? (
                <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 12 }}>No chores set up yet. Add one below!</div>
              ) : (
                chores.map((c: any) => (
                  <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                    <span style={{ fontSize: 22 }}>{c.emoji}</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 800, color: "#1f2937" }}>{c.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 900, color: "#f59e0b" }}>{c.coins} 🪙</span>
                    <button onClick={() => removeChore(c.id)} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#ef4444", padding: "2px 6px" }}>✕</button>
                  </div>
                ))
              )}
              <div style={{ marginTop: 14, background: "#f0fdf4", borderRadius: 14, padding: "12px 14px", border: "1.5px solid #bbf7d0" }}>
                <div style={{ fontSize: 13, fontWeight: 900, color: "#059669", marginBottom: 10 }}>+ Add a Chore</div>
                <input value={newChoreName} onChange={e => setNewChoreName(e.target.value)} placeholder="Chore name (e.g. Wash the dishes)"
                  style={{ width: "100%", border: "1.5px solid #d1fae5", borderRadius: 10, padding: "9px 12px", fontSize: 14, fontFamily: "Nunito", fontWeight: 700, outline: "none", boxSizing: "border-box" as const, marginBottom: 10 }} />
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#374151", marginBottom: 4 }}>COINS REWARD</div>
                    <input type="number" value={newChoreCoins} onChange={e => setNewChoreCoins(e.target.value)} min={1} max={100}
                      style={{ width: "100%", border: "1.5px solid #d1fae5", borderRadius: 10, padding: "9px 12px", fontSize: 16, fontFamily: "Fredoka One", fontWeight: 900, color: "#f59e0b", outline: "none", boxSizing: "border-box" as const }} />
                  </div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#374151", marginBottom: 6 }}>EMOJI</div>
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6, marginBottom: 12 }}>
                  {CHORE_EMOJIS.map(e => (
                    <button key={e} onClick={() => setNewChoreEmoji(e)}
                      style={{ fontSize: 20, background: newChoreEmoji === e ? "#dcfce7" : "#f9fafb", border: `2px solid ${newChoreEmoji === e ? "#10b981" : "#e5e7eb"}`, borderRadius: 8, padding: "3px 5px", cursor: "pointer" }}>{e}</button>
                  ))}
                </div>
                <button onClick={addChore} disabled={!newChoreName.trim()}
                  style={{ width: "100%", background: newChoreName.trim() ? "linear-gradient(135deg,#10b981,#059669)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "10px", color: newChoreName.trim() ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 14, cursor: newChoreName.trim() ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                  Add Chore ✓
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Savings goals */}
        {goals.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 18, padding: "16px", marginBottom: 16, border: "2px solid #d1fae5" }}>
            <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 12 }}>💰 Savings Goals</div>
            {goals.map((g: any) => {
              const pct = g.target > 0 ? Math.min(1, g.balance / g.target) : 0;
              return (
                <div key={g.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#374151" }}>{g.emoji} {g.name}</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: "#10b981" }}>${g.balance.toFixed(2)} / ${g.target.toFixed(2)}</span>
                  </div>
                  <div style={{ background: "#f3f4f6", borderRadius: 20, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${pct * 100}%`, height: "100%", background: "linear-gradient(90deg,#10b981,#059669)", borderRadius: 20 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Real-world milestones */}
        <div style={{ background: "#fff", borderRadius: 18, padding: "16px", border: "2px solid #ede9fe" }}>
          <div style={{ fontSize: 16, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>🏅 Real-World Milestones</div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 14 }}>
            Check off real-life achievements — they appear as bonus badges on your child's Progress screen
          </div>
          {PARENT_MILESTONES.map(m => {
            const checked = !!(parentData?.milestones?.[m.id]);
            return (
              <div key={m.id} onClick={() => toggleMilestone(m.id)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: "1px solid #f3f4f6", cursor: "pointer" }}>
                <div style={{ fontSize: 26 }}>{m.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#1f2937" }}>{m.label}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>{m.desc}</div>
                </div>
                <div style={{ fontSize: 22, transition: "transform 0.15s", transform: checked ? "scale(1.1)" : "scale(1)" }}>{checked ? "✅" : "⬜"}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
